import { useEffect, useState } from 'react';

export type KpiPeriod = 'total' | '30d';

const STORAGE_PREFIX = 'rfo:dashboard:kpi-period:';
const listenersByKey: Record<string, Set<(p: KpiPeriod) => void>> = {};
const stateByKey: Record<string, KpiPeriod> = {};

function readStored(key: string): KpiPeriod {
  if (typeof window === 'undefined') return 'total';
  const v = window.localStorage.getItem(STORAGE_PREFIX + key);
  return v === '30d' ? '30d' : 'total';
}

function setForKey(key: string, next: KpiPeriod) {
  stateByKey[key] = next;
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_PREFIX + key, next);
  }
  const subs = listenersByKey[key];
  if (subs) for (const fn of subs) fn(next);
}

/**
 * Per-widget periode-state. Hver widget bruger sin egen `key` saa
 * deres togglevalg er uafhaengige og persisteres separat i localStorage.
 *
 * @param key Unik nøgle pr. widget — fx 'utilization' eller 'rentals'
 */
export function useKpiPeriod(key: string): [KpiPeriod, (p: KpiPeriod) => void] {
  if (!(key in stateByKey)) stateByKey[key] = readStored(key);

  const [period, setLocal] = useState<KpiPeriod>(stateByKey[key]);

  useEffect(() => {
    if (!listenersByKey[key]) listenersByKey[key] = new Set();
    listenersByKey[key].add(setLocal);
    return () => { listenersByKey[key].delete(setLocal); };
  }, [key]);

  return [period, (next) => setForKey(key, next)];
}
